import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import BoiteOrga from "./BoiteOrga";

/**
 * Page d'arrivée depuis le logiciel métier iVoirNet.
 * Accessible uniquement depuis l'encart iVoirNet — non référencée (noindex).
 * Volontairement hors PublicShell : aucune navigation MetaConnect,
 * le professionnel ne doit pas sortir du parcours de souscription.
 */

const CONNEXION_URL = "https://ohmyvue.fr/login";

const POINTS = [
  {
    t: "Vous ouvrez sur une journée déjà organisée",
    d: "Vos créneaux sont publiés sur votre page. Le patient choisit sa prestation, son praticien et son horaire — un dimanche soir s'il le veut. Vous n'avez plus qu'à préparer les dossiers de la journée.",
    li: [
      "Réservation ouverte 24 h/24, y compris hors horaires",
      "Confirmation puis rappel envoyés au patient, sans vous",
      "Un agenda par professionnel rattaché, sans supplément",
      "Moins de créneaux perdus, moins de rendez-vous oubliés",
    ],
    link: { to: "/agenda-type", label: "Voir un agenda type, opticien et audio" },
    before: "Aujourd'hui : le patient qui appelle à 20 h tombe sur un répondeur.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 11h18" />
        <path d="M8.5 15.5l2 2 4-4" />
      </svg>
    ),
  },
  {
    t: "Le patient arrive avec son dossier déjà prêt",
    d: "Plus aucun document sur une boîte Gmail. Depuis la page de votre établissement, le patient téléverse lui-même ce dont vous avez besoin : ordonnance, carte de mutuelle, attestation de Sécurité sociale, certificats d'authenticité verres, monture ou lentilles. Les documents remontent dans iVoirNet, rattachés à sa fiche, avant qu'il pousse la porte.",
    li: [
      "Un dépôt sécurisé depuis votre page, pas une boîte grand public",
      "Ordonnance, mutuelle, attestation, certificats d'authenticité",
      "Identité vérifiée : le document ne se trompe pas de fiche",
      "Disponible dans iVoirNet, sans scan ni renommage",
    ],
    link: {
      to: "/dossier-patient",
      label: "Voir ce que le patient prépare avant de venir",
    },
    before: "Aujourd'hui : une photo par SMS, une pièce jointe sur Gmail, un scan à renommer.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 3v5h5" />
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M12 17.5v-6M9.5 14l2.5-2.5L14.5 14" />
      </svg>
    ),
  },
  {
    t: "Partager avec l'ensemble des professionnels de santé n'a jamais été aussi simple",
    d: "Ophtalmologiste, orthoptiste, médecin traitant, hôpital, clinique, confrère opticien ou audioprothésiste : le même geste pour tous. iVoirNet prépare le courrier, patient et correspondant déjà renseignés, corrections comprises. Vous relisez, vous adaptez, vous envoyez depuis OhMyVue. Le message part de la boîte de votre établissement et arrive dans la boîte sécurisée du destinataire.",
    li: [
      "Le correspondant se trouve dans l'annuaire santé, sans le chercher",
      "Patient, corrections et date de prescription repris d'iVoirNet",
      "Un format structuré, repris directement dans leur logiciel métier",
      "Une preuve d'envoi horodatée revient dans votre dossier",
    ],
    link: {
      to: "/message-type",
      label: "Voir le message envoyé à l'ophtalmologiste ou à l'orthoptiste",
    },
    before: "Aujourd'hui : un fax, un courrier, ou un mail dont on ignore le sort.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 4L3 11l7 3 3 7z" />
        <path d="M21 4l-11 10" />
      </svg>
    ),
  },
  {
    t: "Le dossier médical partagé, en consultation et en alimentation",
    d: "Vous accédez à Mon espace santé et au dossier médical partagé dans les deux sens : vous consultez ce que les autres professionnels y ont déposé, et vous l'alimentez à votre tour. C'est ce qui rend le suivi des soins continu et la coordination entre les 3 O — ophtalmologiste, orthoptiste, opticien — enfin réelle.",
    li: [
      "Consultation : dernière ordonnance, comptes rendus, historique",
      "Alimentation : vos adaptations déposées au moment de l'envoi",
      "Le patient retrouve ses documents dans Mon espace santé",
      "Une trace horodatée de ce que vous avez fait, et quand",
    ],
    link: {
      to: "/dmp",
      label: "Voir comment on consulte et on alimente le dossier",
    },
    before: "Aujourd'hui : chacun garde ses documents dans son coin.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 21s7.5-4.2 7.5-10V5.5L12 2.7 4.5 5.5V11c0 5.8 7.5 10 7.5 10z" />
        <path d="M9.2 12l2 2 3.6-3.8" />
      </svg>
    ),
  },
];

const STEPS = [
  {
    n: "01",
    t: "Vous vous connectez",
    d: "Carte CPS ou application e-CPS. Cette seule action vous identifie et identifie votre établissement. Aucun mot de passe.",
  },
  {
    n: "02",
    t: "Vous choisissez votre offre",
    d: "Établissement déjà renseigné, signature électronique, paiement par carte. Contrat et facture suivent.",
  },
  {
    n: "03",
    t: "Nous ouvrons votre boîte",
    d: "Vous déclarez vos professionnels, nous créons la messagerie. L'encart iVoirNet passe en « Connecté ».",
  },
];

const OFFERS = [
  {
    name: "Messagerie de Santé organisationnelle",
    price: "19,99 €",
    featured: false,
    items: [
      "Votre messagerie sécurisée de santé",
      "Tous vos professionnels rattachés, sans supplément",
      "Envois et réceptions illimités, traçabilité horodatée",
      "Visibilité dans l'annuaire santé",
      "Site vitrine personnalisable inclus",
      "Intégration iVoirNet incluse",
    ],
  },
  {
    name: "Messagerie + Agenda",
    price: "49,99 €",
    featured: true,
    items: [
      "Tout ce que contient l'offre précédente",
      "Agenda connecté, interopérable avec vos activités",
      "Prise de rendez-vous en ligne",
      "Un agenda par professionnel rattaché",
      "Rappels automatiques aux patients",
    ],
  },
  {
    name: "Messagerie + Agenda + Suivi myopie",
    logo: "/logos/myo-control.jpeg",
    soon: true,
    price: "79,99 €",
    featured: false,
    items: [
      "Tout ce que contient l'offre précédente",
      "Module Myo-Control pour la freination myopique",
      "Passeports et carnets digitalisés",
      "Document unique alimentant le DMP",
    ],
  },
];

const SOCLES = [
  { src: "/logos/insi.png", alt: "INSi — Identité Nationale de Santé" },
  { src: "/logos/mssante-official.png", alt: "MSSanté" },
  { src: "/logos/dmp.png", alt: "Dossier Médical Partagé" },
  { src: "/logos/mon-espace-sante.png", alt: "Mon espace santé" },
  { src: "/logos/psc.png", alt: "Pro Santé Connect" },
  { src: "/logos/hds.png", alt: "Hébergeur de données de santé certifié" },
];

const MORE = [
  { t: "L'agenda et la prise de rendez-vous en ligne", to: "/agenda" },
  { t: "Le site vitrine inclus dans l'offre", to: "/agenda#vitrine" },
  { t: "La MSSanté pour les opticiens", to: "/mssante/opticien" },
  { t: "La MSSanté pour les audioprothésistes", to: "/mssante/audioprothesiste" },
  { t: "L'identité nationale de santé", to: "/services/ins" },
  { t: "Le dossier médical partagé", to: "/services/dmp" },
  { t: "Pro Santé Connect et la carte CPS", to: "/services/psc" },
  { t: "Conformité et hébergement", to: "/conformite" },
];

const Arrow = () => (
  <svg className="ivn-arw" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Tick = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

export default function IVoirNet() {
  return (
    <div className="ivn-osm">
      <SEO
        title="iVoirNet × OhMyVue — vos fonctionnalités e-santé ouvertes depuis iVoirNet"
        description="Page réservée aux opticiens et audioprothésistes équipés d'iVoirNet : messagerie sécurisée de santé, agenda connecté et prise de rendez-vous en ligne, espace patient, dossier médical partagé — activés avec votre carte CPS."
        canonical="/ivoirnet"
        noIndex
      />

      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap"
        />
      </Helmet>

      <style>{CSS}</style>

      {/* ---------------- barre haute ---------------- */}
      <header className="ivn-topbar">
        <div className="ivn-wrap">
          <img className="ivn-mark" src="/logos/ohmyvue.png" alt="OhMyVue" />
          <span className="ivn-spacer" />
          <a className="ivn-btn ivn-btn-primary ivn-btn-sm" href={CONNEXION_URL}>
            Connexion
          </a>
        </div>
      </header>

      {/* ---------------- hero : le partenariat ---------------- */}
      <div className="ivn-hero">
        <div className="ivn-wrap">
          <div className="ivn-lockup">
            <span className="ivn-plate">
              <img className="ivn-osmose" src="/logos/ivoirnet.png" alt="iVoirNet" />
            </span>
            <span className="ivn-x" aria-hidden="true">
              ×
            </span>
            <img className="ivn-omv" src="/logos/ohmyvue.png" alt="OhMyVue" />
            <div className="ivn-tag">Partenariat technologique</div>
          </div>

          <h1>
            Toutes vos fonctionnalités e-santé, <em>ouvertes depuis iVoirNet.</em>
          </h1>
          <p className="ivn-lede">
            <b>Messagerie sécurisée de santé</b>, <b>agenda connecté et prise de rendez-vous en
            ligne</b>, <b>espace patient pour le dépôt de documents</b>, <b>consultation et
            alimentation du dossier médical partagé</b> : tout s'active depuis iVoirNet, sans changer
            de logiciel ni ressaisir quoi que ce soit.
          </p>
          <p className="ivn-claim">
            Vous n'ajoutez pas un logiciel de plus. Vous entrez dans le réseau où ophtalmologistes,
            orthoptistes et opticiens se répondent enfin.
          </p>

          <div className="ivn-actions">
            <a className="ivn-btn ivn-btn-primary ivn-btn-lg" href={CONNEXION_URL}>
              Connexion <Arrow />
            </a>
            <a className="ivn-btn ivn-btn-ghost-dark ivn-btn-lg" href="#offres">
              Voir les offres
            </a>
          </div>
        </div>
      </div>

      {/* ---------------- bande services socles ---------------- */}
      <div className="ivn-strip">
        <div className="ivn-wrap">
          <p className="ivn-strip-lead">
            OhMyVue est raccordé aux services socles de l'État. L'identité est qualifiée, le message
            transmis, le document déposé.
          </p>
          <div className="ivn-marks">
            {SOCLES.map((s) => (
              <img key={s.src} src={s.src} alt={s.alt} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* ---------------- la boîte de l'établissement ---------------- */}
      <section className="ivn-sec">
        <div className="ivn-wrap">
          <div className="ivn-head">
            <span className="ivn-pill ivn-pill-light">Messagerie sécurisée de santé</span>
            <h2>Une seule boîte, pour tout l'établissement</h2>
            <p>
              Une adresse qui appartient au magasin, pas à une personne. Vos professionnels y sont
              rattachés, tout le monde voit le même flux, et chaque envoi porte le nom de celui qui
              l'a envoyé.
            </p>
          </div>
          <BoiteOrga />
        </div>
      </section>

      {/* ---------------- trois bénéfices ---------------- */}
      <section className="ivn-sec">
        <div className="ivn-wrap">
          <div className="ivn-head">
            <span className="ivn-pill ivn-pill-light">Ce que ça change</span>
            <h2>Quatre choses qui changent votre journée</h2>
            <p>
              Vous gardez iVoirNet et vos habitudes. Ce qui change, c'est ce qui entre et ce qui sort
              de votre magasin.
            </p>
          </div>
          <div className="ivn-four">
            {POINTS.map((p) => (
              <div className="ivn-pt" key={p.t}>
                <div className="ivn-ic">{p.icon}</div>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
                <ul>
                  {p.li.map((x) => (
                    <li key={x}>
                      <Tick />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
                {p.link && (
                  <Link className="ivn-seelink" to={p.link.to}>
                    {p.link.label} <Arrow />
                  </Link>
                )}
                <span className="ivn-before">{p.before}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- démarrer ---------------- */}
      <section className="ivn-sec ivn-dark">
        <div className="ivn-wrap">
          <div className="ivn-head">
            <span className="ivn-pill ivn-pill-dark">Démarrer</span>
            <h2>Trois étapes, votre carte CPS suffit</h2>
          </div>
          <ol className="ivn-flow">
            {STEPS.map((s) => (
              <li className="ivn-fl" key={s.n}>
                <div className="ivn-n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </li>
            ))}
          </ol>
          <p className="ivn-flow-note">
            Activation sous 48 heures ouvrées. Vos professionnels sont rattachés sans limite de nombre
            ni supplément.
          </p>
        </div>
      </section>

      {/* ---------------- offres ---------------- */}
      <section className="ivn-sec" id="offres">
        <div className="ivn-wrap">
          <div className="ivn-head">
            <span className="ivn-pill ivn-pill-light">Tarifs</span>
            <h2>Un prix par établissement</h2>
          </div>
          <div className="ivn-offers">
            {OFFERS.map((o) => (
              <div className={"ivn-offer" + (o.featured ? " ivn-feat" : "") + (o.soon ? " ivn-bientot" : "")} key={o.name}>
                {o.featured && <span className="ivn-badge">La plus complète</span>}
                {o.soon && <span className="ivn-badge ivn-badge-soon">Bientôt disponible</span>}
                <div className="ivn-oname">{o.name}</div>
                <div className="ivn-price">
                  <b>{o.price}</b>
                  <span>/ mois HT</span>
                </div>
                <div className="ivn-osub">Par établissement, engagement 12 mois</div>
                {o.logo && (<div className="ivn-omodule"><img src={o.logo} alt="Module Myo-Control" /></div>)}
                <ul>
                  {o.items.map((it) => (
                    <li key={it}>
                      <Tick />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                {o.soon && (<div className="ivn-onote">Module en cours de finalisation. Activation possible depuis votre espace OhMyVue dès son ouverture.</div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- le choix se fait après la connexion ---------------- */}
      <section className="ivn-confirm">
        <div className="ivn-wrap">
          <div className="ivn-cb-txt">
            <div className="ivn-cb-k">Important</div>
            <h2>Le choix de votre offre se fait après la connexion.</h2>
            <p>
              Ces encarts vous présentent le contenu de chaque offre. La sélection et le paiement ont
              lieu sur la plateforme OhMyVue, une fois votre établissement identifié par Pro Santé
              Connect — c'est là que vous choisirez, et nulle part ailleurs.
            </p>
          </div>
          <div className="ivn-actions">
            <a className="ivn-btn ivn-btn-white ivn-btn-lg" href={CONNEXION_URL}>
              Connexion <Arrow />
            </a>
            <Link className="ivn-btn ivn-btn-ghostw ivn-btn-lg" to="/demo">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      
      {/* ---------------- pour aller plus loin ---------------- */}
      <section className="ivn-sec ivn-more-sec">
        <div className="ivn-wrap">
          <div className="ivn-head">
            <span className="ivn-pill ivn-pill-light">Pour aller plus loin</span>
            <h2>Tout comprendre, sur metaconnect.fr</h2>
          </div>
          <div className="ivn-more">
            {MORE.map((m) => (
              <Link className="ivn-lnk" to={m.to} target="_blank" rel="noopener" key={m.to}>
                <span>
                  <span className="ivn-lt">{m.t}</span>
                  <span className="ivn-lu">metaconnect.fr{m.to}</span>
                </span>
                <span className="ivn-go">
                  <Arrow />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- pied de page ---------------- */}
      <footer className="ivn-foot">
        <div className="ivn-wrap">
          <div className="ivn-op">
            <img src="/logos/ohmyvue.png" alt="OhMyVue" />
            <span>
              Service opéré par MetaConnect.fr, opérateur MSSanté référencé auprès de l'ANS.
            </span>
          </div>
          <div className="ivn-links">
            <Link to="/mentions-legales">Mentions légales</Link>
            <Link to="/cgv">CGV</Link>
            <Link to="/cgu">CGU</Link>
            <a href="mailto:contact@metaconnect.fr">contact@metaconnect.fr</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Styles portés par la page. Tout est préfixé .ivn-osm : rien ne fuit vers
   le reste du site, et les règles globales sur h1..h6 sont neutralisées.
   Charte reprise de ohmyvue.fr : Instrument Sans, bandes #0F172A / #F5F5F5,
   primaire #0080FF, boutons entièrement arrondis.
   ------------------------------------------------------------------------- */
const CSS = `
.ivn-osm{
  --ink:#0F172A; --ink2:#1B2540; --ground:#F5F5F5; --surface:#FFFFFF;
  --line:#E6E8EC; --blue:#0080FF; --blue700:#0064CC; --bsoft:#EFF6FF;
  --bline:#D6E7FB; --text:#0F172A; --muted:#5A6577; --mutedd:#A8B3C7; --ok:#0FA36B;
  background:var(--ground); color:var(--text); min-height:100vh;
  font-family:"Instrument Sans",ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;
  font-size:15.5px; line-height:1.6; -webkit-font-smoothing:antialiased;
}
.ivn-osm *{box-sizing:border-box}
.ivn-osm img{max-width:100%}
.ivn-wrap{max-width:1080px;margin:0 auto;padding:0 28px}
.ivn-osm h1,.ivn-osm h2,.ivn-osm h3{margin:0;text-wrap:balance;color:var(--text);font-weight:600}
.ivn-osm h1{font-size:clamp(31px,4vw,44px);line-height:1.12;letter-spacing:-.02em}
.ivn-osm h2{font-size:clamp(23px,2.4vw,29px);line-height:1.2;letter-spacing:-.015em}
.ivn-osm h3{font-size:16.5px;line-height:1.35}
.ivn-osm p{margin:0;color:var(--muted)}
.ivn-osm ul,.ivn-osm ol{list-style:none;margin:0;padding:0}
.ivn-dark{background:var(--ink)}
.ivn-dark h2,.ivn-dark h3{color:#fff}
.ivn-dark p{color:var(--mutedd)}

.ivn-btn{display:inline-flex;align-items:center;gap:9px;border-radius:999px;font-weight:600;
  font-size:14px;text-decoration:none;border:1px solid transparent;padding:11px 22px;
  transition:background .15s,border-color .15s;cursor:pointer}
.ivn-btn-primary{background:var(--blue);color:#fff}
.ivn-btn-primary:hover{background:var(--blue700);color:#fff}
.ivn-btn-ghost-dark{border-color:rgba(255,255,255,.24);color:#fff}
.ivn-btn-ghost-dark:hover{border-color:rgba(255,255,255,.5);color:#fff}
.ivn-btn-white{background:#fff;color:var(--blue);font-size:15.5px;padding:15px 30px}
.ivn-btn-white:hover{background:#EAF4FF;color:var(--blue)}
.ivn-btn-lg{padding:14px 28px;font-size:15px}
.ivn-btn-sm{padding:9px 18px;font-size:13.5px}
.ivn-arw{width:15px;height:15px;stroke:currentColor;fill:none;stroke-width:2;
  stroke-linecap:round;stroke-linejoin:round;flex:0 0 15px}

.ivn-pill{display:inline-flex;align-items:center;border-radius:999px;font-size:11.5px;
  font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.ivn-pill-dark{background:rgba(255,255,255,.09);color:#DCE5F5}
.ivn-pill-light{background:#E7ECF3;color:#45526B}

/* barre haute */
.ivn-topbar{background:var(--ink)}
.ivn-topbar .ivn-wrap{display:flex;align-items:center;height:60px}
.ivn-mark{height:19px;width:auto;filter:brightness(0) invert(1)}
.ivn-spacer{flex:1}

/* hero */
.ivn-hero{background:var(--ink);position:relative;overflow:hidden}
.ivn-hero::after{content:"";position:absolute;right:-200px;top:-280px;width:720px;height:720px;
  border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(0,128,255,.18) 0%,rgba(0,128,255,0) 62%)}
.ivn-hero .ivn-wrap{padding:46px 28px 54px;position:relative;z-index:1}
.ivn-lockup{display:flex;align-items:center;gap:34px;flex-wrap:wrap;padding-bottom:38px;
  border-bottom:1px solid rgba(255,255,255,.10);margin-bottom:40px}
.ivn-plate{display:inline-flex;align-items:center;justify-content:center;background:transparent;
  border-radius:0;padding:0;box-shadow:none}
.ivn-osmose{height:176px;width:auto;display:block;
  filter:drop-shadow(0 0 1.5px rgba(255,255,255,.85)) drop-shadow(0 0 8px rgba(190,225,255,.45))
    drop-shadow(0 0 34px rgba(95,179,255,.24)) drop-shadow(0 16px 30px rgba(0,0,0,.45))}
.ivn-omv{height:62px;width:auto;filter:brightness(0) invert(1)}
.ivn-x{width:46px;height:46px;flex:0 0 46px;border-radius:50%;display:grid;place-items:center;
  border:1px solid rgba(255,255,255,.22);color:rgba(255,255,255,.65);font-size:21px}
.ivn-tag{margin-left:auto;font-size:11.5px;font-weight:600;letter-spacing:.11em;
  text-transform:uppercase;color:#5FB3FF}
.ivn-hero h1{color:#fff;max-width:17ch;margin-bottom:18px}
.ivn-hero h1 em{font-style:normal;color:#5FB3FF}
.ivn-lede{max-width:54ch}
.ivn-hero .ivn-lede{font-size:19px;line-height:1.62;color:#AEC1DC}
.ivn-hero .ivn-lede b{color:#fff;font-weight:600}
.ivn-claim{max-width:52ch;margin-top:18px;font-size:16px;font-weight:600;color:#fff;
  padding-left:16px;border-left:2px solid var(--blue);line-height:1.5}
.ivn-actions{display:flex;flex-wrap:wrap;gap:12px;margin-top:28px}

/* bande services socles */
.ivn-strip{background:var(--surface);border-bottom:1px solid var(--line);padding:26px 0}
.ivn-strip .ivn-wrap{display:flex;align-items:center;gap:28px;flex-wrap:wrap}
.ivn-strip-lead{font-size:13.4px;color:var(--muted);max-width:34ch;flex:1 1 260px;line-height:1.5}
.ivn-marks{display:flex;align-items:center;gap:26px;flex-wrap:wrap}
.ivn-marks img{height:32px;width:auto;object-fit:contain;opacity:.92}

.ivn-sec{padding:66px 0}
.ivn-head{max-width:58ch;margin-bottom:32px}
.ivn-head h2{margin-top:14px}

/* trois bénéfices */
.ivn-three{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.ivn-pt{background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:24px 22px}
.ivn-ic{width:38px;height:38px;border-radius:11px;display:grid;place-items:center;margin-bottom:15px;
  background:var(--bsoft);border:1px solid var(--bline)}
.ivn-ic svg{width:18px;height:18px;stroke:var(--blue);fill:none;stroke-width:1.9;
  stroke-linecap:round;stroke-linejoin:round}
.ivn-pt h3{margin-bottom:8px}
.ivn-pt p{font-size:13.8px;line-height:1.55}

/* démarrer */
.ivn-flow{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.ivn-fl{background:var(--ink2);border:1px solid rgba(255,255,255,.09);border-radius:14px;padding:22px 20px}
.ivn-n{font-size:12px;font-weight:600;color:#5FB3FF;letter-spacing:.08em;font-variant-numeric:tabular-nums}
.ivn-fl h3{margin:9px 0 7px;font-size:15.5px;color:#fff}
.ivn-fl p{font-size:13.6px;line-height:1.55;color:var(--mutedd)}
.ivn-flow-note{margin-top:18px;font-size:13.4px;color:#8F9DB4}

/* offres */
.ivn-offers{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;align-items:stretch}
.ivn-offer{background:var(--surface);border:1px solid var(--line);border-radius:18px;
  padding:26px 24px;display:flex;flex-direction:column;position:relative}
.ivn-feat{border-color:var(--blue);box-shadow:0 12px 34px -18px rgba(0,128,255,.55)}
.ivn-badge{position:absolute;top:-11px;left:24px;background:var(--blue);color:#fff;border-radius:999px;
  font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:5px 13px}
.ivn-oname{font-size:15.5px;font-weight:600;line-height:1.3;min-height:2.6em}
.ivn-price{display:flex;align-items:baseline;gap:7px;margin-top:14px}
.ivn-price b{font-size:33px;font-weight:600;letter-spacing:-.02em;font-variant-numeric:tabular-nums}
.ivn-price span{font-size:13.5px;color:var(--muted)}
.ivn-osub{font-size:12.4px;color:var(--muted);margin-top:3px}
.ivn-omodule{margin-top:16px;padding-top:14px;border-top:1px dashed var(--line)}
.ivn-omodule img{height:50px;width:auto;display:block}
.ivn-offer ul{margin:20px 0 0;display:grid;gap:9px;flex:1;align-content:start}
.ivn-offer li{display:flex;gap:10px;font-size:13.5px;line-height:1.5;color:#3D4759}
.ivn-offer li svg{width:14px;height:14px;flex:0 0 14px;margin-top:4px;stroke:var(--ok);fill:none;
  stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}

/* bandeau : le choix se fait après la connexion */
.ivn-confirm{background:var(--blue);padding:52px 0}
.ivn-confirm .ivn-wrap{display:flex;align-items:center;gap:36px;flex-wrap:wrap;
  justify-content:space-between}
.ivn-cb-txt{flex:1 1 480px}
.ivn-cb-k{font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;
  color:rgba(255,255,255,.72);margin-bottom:12px}
.ivn-confirm h2{color:#fff;font-size:clamp(25px,3vw,34px);margin-bottom:14px}
.ivn-confirm p{color:rgba(255,255,255,.88);font-size:15.5px;max-width:62ch;line-height:1.6}

/* connexion */
.ivn-connect .ivn-wrap{display:grid;grid-template-columns:1.05fr .95fr;gap:36px;align-items:center}
.ivn-connect h2{margin:16px 0 14px}
.ivn-connect p{max-width:50ch}
.ivn-sim{background:var(--ink2);border:1px solid rgba(255,255,255,.10);border-radius:18px;padding:26px}
.ivn-sim-hd{display:flex;align-items:center;gap:12px;padding-bottom:16px;
  border-bottom:1px solid rgba(255,255,255,.10);margin-bottom:18px}
.ivn-sim-hd img{height:17px;width:auto;filter:brightness(0) invert(1)}
.ivn-sim-hd span{font-size:12.5px;color:#8F9DB4;margin-left:auto}
.ivn-sim-grid{display:grid;gap:15px}
.ivn-lab{font-size:11.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#7F8DA5}
.ivn-val{font-size:15px;font-weight:600;color:#fff;margin-top:4px}
.ivn-sim-psc{margin-top:19px;display:flex;align-items:center;gap:12px;
  background:rgba(0,128,255,.12);border:1px solid rgba(0,128,255,.32);border-radius:12px;padding:14px 16px}
.ivn-sim-psc svg{width:19px;height:19px;flex:0 0 19px;stroke:#5FB3FF;fill:none;stroke-width:1.9;
  stroke-linecap:round;stroke-linejoin:round}
.ivn-sim-psc div{font-size:13.4px;color:#CFDCEF;line-height:1.45}

/* pour aller plus loin */
.ivn-more-sec{background:var(--surface);border-top:1px solid var(--line)}
.ivn-more{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.ivn-lnk{display:flex;align-items:center;gap:14px;background:var(--ground);
  border:1px solid var(--line);border-radius:13px;padding:16px 18px;text-decoration:none;
  color:var(--text);transition:border-color .15s,transform .15s}
.ivn-lnk:hover{border-color:var(--blue);transform:translateY(-1px);color:var(--text)}
.ivn-lt{display:block;font-size:14.2px;font-weight:600;line-height:1.3}
.ivn-lu{display:block;font-size:12.4px;color:var(--muted);margin-top:2px}
.ivn-go{margin-left:auto;color:var(--blue);display:grid;place-items:center;flex:0 0 auto}

/* pied de page */
.ivn-foot{background:#0A1120;color:#8C99AD;padding:34px 0;font-size:13px}
.ivn-foot .ivn-wrap{display:flex;flex-wrap:wrap;gap:18px;align-items:center;
  justify-content:space-between}
.ivn-op{display:flex;align-items:center;gap:11px;max-width:58ch}
.ivn-op img{height:17px;width:auto;opacity:.75;filter:brightness(0) invert(1)}
.ivn-links{display:flex;gap:18px;flex-wrap:wrap}
.ivn-foot a{color:#B7C3D8;text-decoration:none}
.ivn-foot a:hover{text-decoration:underline;color:#fff}

@media (max-width:900px){
  .ivn-three,.ivn-flow,.ivn-offers,.ivn-more{grid-template-columns:1fr}
  .ivn-connect .ivn-wrap{grid-template-columns:1fr}
  .ivn-osmose{height:122px}
  .ivn-omv{height:46px}
  .ivn-x{width:38px;height:38px;flex:0 0 38px;font-size:18px}
  .ivn-plate{padding:0}
  .ivn-tag{margin-left:0;width:100%}
  .ivn-marks{gap:20px}
  .ivn-marks img{height:26px}
}
@media (prefers-reduced-motion:reduce){.ivn-osm *{transition:none !important}}
.ivn-osm a:focus-visible,.ivn-osm button:focus-visible{outline:2px solid var(--blue);
  outline-offset:3px;border-radius:6px}

/* ---- offre en preparation ---- */
.ivn-bientot{background:#FBFBFC;border-style:dashed;border-color:#D8DEE8}
.ivn-bientot .ivn-oname,.ivn-bientot .ivn-price b{color:#59637A}
.ivn-bientot li{color:#6C7588}.ivn-bientot li svg{stroke:#9AA6B8}
.ivn-bientot .ivn-omodule img{opacity:.9}
.ivn-badge-soon{background:#E7ECF3;color:#45526B;border:1px solid #D2DAE6}
.ivn-onote{margin-top:16px;padding-top:13px;border-top:1px dashed var(--line);
  font-size:12.6px;line-height:1.5;color:#6C7588;text-align:center}

/* ---- composition centree ---- */
.ivn-hero::after{right:auto;left:50%;transform:translateX(-50%);top:-380px;width:940px;height:940px}
.ivn-hero .ivn-wrap{text-align:center;padding:54px 28px 62px}
.ivn-lockup{justify-content:center;gap:30px;padding-bottom:28px;margin-bottom:30px}
.ivn-tag{order:-1;flex:0 0 100%;margin-left:0;text-align:center;margin-bottom:2px}
.ivn-hero h1{max-width:19ch;margin-left:auto;margin-right:auto}
.ivn-lede{max-width:60ch;margin-left:auto;margin-right:auto}
.ivn-claim{max-width:56ch;margin:22px auto 0;padding:15px 22px;border-left:0;border-radius:14px;
  background:rgba(0,128,255,.10);border:1px solid rgba(95,179,255,.30);text-align:center}
.ivn-actions{justify-content:center;margin-top:30px}
.ivn-strip .ivn-wrap{flex-direction:column;text-align:center;gap:18px}
.ivn-strip-lead{flex:none;max-width:62ch}
.ivn-marks{justify-content:center}
.ivn-head{max-width:62ch;margin-left:auto;margin-right:auto;text-align:center}
.ivn-pt{text-align:center}
.ivn-ic{margin-left:auto;margin-right:auto}
.ivn-fl{text-align:center}
.ivn-flow-note{text-align:center}
.ivn-oname,.ivn-osub{text-align:center}
.ivn-price{justify-content:center}
.ivn-omodule img{margin-left:auto;margin-right:auto}
.ivn-badge{left:50%;transform:translateX(-50%)}
.ivn-confirm .ivn-wrap{flex-direction:column;justify-content:center;text-align:center;gap:26px}
.ivn-cb-txt{flex:none;max-width:70ch}
.ivn-confirm p{margin-left:auto;margin-right:auto}
.ivn-connect .ivn-wrap{grid-template-columns:1fr;justify-items:center;text-align:center;gap:32px}
.ivn-connect p{margin-left:auto;margin-right:auto}
.ivn-sim{width:100%;max-width:520px;text-align:left}
.ivn-foot .ivn-wrap{justify-content:center;text-align:center;flex-direction:column;gap:14px}
.ivn-op,.ivn-links{justify-content:center}

/* ---- connexion : panneau ---- */
.ivn-connect{position:relative;overflow:hidden}
.ivn-connect::before{content:"";position:absolute;left:50%;top:34%;transform:translateX(-50%);
  width:860px;height:540px;border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(0,128,255,.17) 0%,rgba(0,128,255,0) 66%)}
.ivn-connect .ivn-wrap{position:relative;z-index:1;gap:34px}
.ivn-sim{max-width:660px;padding:0;overflow:hidden;border-radius:20px;
  background:linear-gradient(180deg,#17223C 0%,#101A2F 100%);
  border:1px solid rgba(255,255,255,.14);
  box-shadow:0 34px 72px -34px rgba(0,110,235,.55)}
.ivn-sim-hd{margin:0;padding:15px 22px;background:rgba(255,255,255,.045);
  border-bottom:1px solid rgba(255,255,255,.09)}
.ivn-sim-grid{gap:0;padding:4px 22px}
.ivn-sim-grid>div{display:flex;align-items:baseline;justify-content:space-between;gap:20px;
  padding:14px 0;border-bottom:1px solid rgba(255,255,255,.07)}
.ivn-sim-grid>div:last-child{border-bottom:0}
.ivn-lab{flex:0 0 auto}
.ivn-val{margin-top:0;text-align:right;font-size:14px}
.ivn-sim-psc{margin:2px 22px 22px}
.ivn-btn-ghostw{border-color:rgba(255,255,255,.55);color:#fff}
.ivn-btn-ghostw:hover{border-color:#fff;color:#fff}
.ivn-osm .ivn-lede{margin-left:auto;margin-right:auto}
.ivn-osm .ivn-claim{margin:22px auto 0}
.ivn-osm .ivn-strip-lead{margin-left:auto;margin-right:auto}
.ivn-osm .ivn-claim{color:#fff;font-weight:600}

/* ---- quatre encarts ---- */
.ivn-four{display:grid;grid-template-columns:repeat(2,1fr);gap:18px;align-items:stretch}
.ivn-osm .ivn-pt{display:flex;flex-direction:column;padding:28px 26px;text-align:center}
.ivn-osm .ivn-pt h3{font-size:18px;line-height:1.3;margin-bottom:10px}
.ivn-osm .ivn-pt p{font-size:14px;line-height:1.6;max-width:52ch;margin-left:auto;margin-right:auto}
.ivn-osm .ivn-head p{margin-top:12px;font-size:15.5px}
.ivn-osm .ivn-pt ul{margin-top:18px;padding-top:16px;border-top:1px solid var(--line);
  display:grid;gap:10px;text-align:left;flex:1;align-content:start}
.ivn-osm .ivn-pt li{display:flex;gap:10px;font-size:13.6px;line-height:1.5;color:#3D4759}
.ivn-osm .ivn-pt li svg{width:14px;height:14px;flex:0 0 14px;margin-top:4px;stroke:var(--ok);
  fill:none;stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}
.ivn-seelink{display:inline-flex;align-items:center;gap:8px;margin-top:16px;align-self:center;
  font-size:13.6px;font-weight:600;color:var(--blue);text-decoration:none;
  border:1px solid var(--bline);background:var(--bsoft);border-radius:999px;padding:9px 17px}
.ivn-seelink:hover{background:#DCEDFF;color:var(--blue)}
.ivn-before{display:block;margin-top:15px;padding-top:12px;border-top:1px dashed var(--line);
  font-size:12.6px;color:#7C8698;font-style:italic;text-align:left}
@media (max-width:900px){.ivn-four{grid-template-columns:1fr}}
`;
